"use client";

import { useEffect, useState } from "react";
import {
  deletePreferenceMemory,
  listPreferenceMemories,
  updatePreferenceMemory,
  type PreferenceMemory,
  type PreferenceMemoryType,
} from "@/lib/settings-api";

// Curated group order (Phase 11-3.b Q5 lock — narrative ramp:
// "how you work → what blocks you → rules you follow → what you're aiming for").
const GROUPS: { type: PreferenceMemoryType; label: string; emptyHint: string }[] = [
  {
    type: "preference",
    label: "How you work",
    emptyHint: "Things like \"I focus best at 8pm\" will show up here.",
  },
  {
    type: "recurring_blocker",
    label: "What gets in your way",
    emptyHint: "Recurring blockers you mention will show up here.",
  },
  {
    type: "decision_rule",
    label: "Rules you follow",
    emptyHint: "Decision rules like \"freedom over money\" will show up here.",
  },
  {
    type: "recurring_ambition",
    label: "What you're aiming for",
    emptyHint: "Recurring ambitions you mention will show up here.",
  },
];

type EditState = { id: string; draft: string } | null;
type RowStatus = "idle" | "saving" | "deleting" | "downgrading";

export default function AIMemorySection() {
  const [memories, setMemories] = useState<PreferenceMemory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditState>(null);
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});

  useEffect(() => {
    listPreferenceMemories()
      .then((rows) => {
        setMemories(rows);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load memories");
        setMemories([]);
      });
  }, []);

  const setStatus = (id: string, status: RowStatus) =>
    setRowStatus((prev) => ({ ...prev, [id]: status }));

  const handleStartEdit = (row: PreferenceMemory) => {
    setEditing({ id: row.id, draft: row.content });
  };

  const handleCancelEdit = () => {
    setEditing(null);
  };

  const handleSaveEdit = async (row: PreferenceMemory) => {
    if (!editing || editing.id !== row.id) return;
    const draft = editing.draft.trim();
    if (!draft) return;
    if (draft === row.content) {
      setEditing(null);
      return;
    }
    setStatus(row.id, "saving");
    try {
      const updated = await updatePreferenceMemory(row.id, { content: draft });
      setMemories((prev) =>
        prev ? prev.map((m) => (m.id === row.id ? updated : m)) : prev,
      );
      setEditing(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setStatus(row.id, "idle");
    }
  };

  const handleNotQuiteRight = async (row: PreferenceMemory) => {
    setStatus(row.id, "downgrading");
    try {
      const updated = await updatePreferenceMemory(row.id, { importance: 0.1 });
      setMemories((prev) =>
        prev ? prev.map((m) => (m.id === row.id ? updated : m)) : prev,
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setStatus(row.id, "idle");
    }
  };

  const handleDelete = async (row: PreferenceMemory) => {
    setStatus(row.id, "deleting");
    try {
      await deletePreferenceMemory(row.id);
      setMemories((prev) => (prev ? prev.filter((m) => m.id !== row.id) : prev));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setStatus(row.id, "idle");
    }
  };

  return (
    <div
      className="bg-card rounded-2xl border border-border shadow-sm p-4"
      data-testid="settings-ai-memory-section"
    >
      <h2 className="text-sm font-semibold text-text-light mb-1">
        What the AI has learned
      </h2>
      <p className="text-xs text-muted mb-3">
        Notes the AI keeps about how you work. Edit, downgrade, or remove anything that isn&apos;t right.
      </p>

      {error && (
        <div
          className="mb-3 p-2 bg-amber/10 border border-amber/30 rounded-lg text-xs text-amber"
          data-testid="ai-memory-error"
        >
          {error}
        </div>
      )}

      {memories === null ? (
        <div className="text-xs text-muted py-2" data-testid="ai-memory-loading">
          Loading…
        </div>
      ) : (
        <div className="space-y-4">
          {GROUPS.map((group) => {
            const rows = memories.filter((m) => m.memory_type === group.type);
            return (
              <section key={group.type} data-testid={`ai-memory-group-${group.type}`}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">
                  {group.label}
                </h3>
                {rows.length === 0 ? (
                  <p
                    className="text-xs text-muted italic"
                    data-testid={`ai-memory-empty-${group.type}`}
                  >
                    {group.emptyHint}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {rows.map((row) => {
                      const status = rowStatus[row.id] ?? "idle";
                      const isEditing = editing?.id === row.id;
                      const downgraded = row.importance <= 0.15;
                      return (
                        <li
                          key={row.id}
                          data-testid={`ai-memory-row-${row.id}`}
                          className={`rounded-xl border p-3 transition-opacity ${
                            downgraded
                              ? "border-border bg-sand-pale/30 opacity-70"
                              : "border-border bg-cream"
                          }`}
                        >
                          {isEditing ? (
                            <div className="space-y-2">
                              <textarea
                                value={editing.draft}
                                onChange={(e) =>
                                  setEditing({ id: row.id, draft: e.target.value })
                                }
                                data-testid={`ai-memory-edit-input-${row.id}`}
                                rows={2}
                                maxLength={500}
                                className="w-full text-sm text-text bg-card border border-border rounded-lg p-2 focus:outline-none focus:border-forest-med"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSaveEdit(row)}
                                  disabled={status === "saving" || !editing.draft.trim()}
                                  data-testid={`ai-memory-save-${row.id}`}
                                  className="text-xs px-3 py-1 bg-forest-med text-white rounded-full disabled:opacity-50"
                                >
                                  {status === "saving" ? "Saving…" : "Save"}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  data-testid={`ai-memory-cancel-${row.id}`}
                                  className="text-xs px-3 py-1 bg-transparent text-text-light rounded-full border border-border"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-text">{row.content}</p>
                              <div className="mt-2 flex flex-wrap gap-2 items-center">
                                <span
                                  className="text-[10px] text-muted"
                                  data-testid={`ai-memory-source-${row.id}`}
                                >
                                  {row.source_agent === "onboarding"
                                    ? "Seeded during onboarding"
                                    : row.source_agent === "chat_extractor"
                                      ? "Learned from chat"
                                      : "Learned"}
                                  {downgraded && " · downgraded"}
                                </span>
                                <span className="flex-1" />
                                <button
                                  onClick={() => handleStartEdit(row)}
                                  data-testid={`ai-memory-edit-${row.id}`}
                                  className="text-xs px-2 py-1 text-forest-med hover:underline"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleNotQuiteRight(row)}
                                  disabled={status === "downgrading" || downgraded}
                                  data-testid={`ai-memory-wrong-${row.id}`}
                                  className="text-xs px-2 py-1 text-text-light hover:underline disabled:opacity-40"
                                >
                                  {status === "downgrading" ? "…" : "Not quite right"}
                                </button>
                                <button
                                  onClick={() => handleDelete(row)}
                                  disabled={status === "deleting"}
                                  data-testid={`ai-memory-delete-${row.id}`}
                                  className="text-xs px-2 py-1 text-rose hover:underline disabled:opacity-40"
                                >
                                  {status === "deleting" ? "…" : "Delete"}
                                </button>
                              </div>
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
